import { InterventionAcces } from '../intervention-acces/intervention-acces';
import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { TicketCardItem } from '../../Services/EntiteProjet';
import {
  dateInterventionDepassee,
  formaterAnciennete,
  formaterDateCarte,
} from './ticket-card-format';

@Component({
  selector: 'app-silo-ticket-item',
  imports: [InterventionAcces],
  templateUrl: './silo-ticket-item.html',
  styleUrl: './silo-ticket-item.css',
})
export class SiloTicketItem {
  readonly ticket = input.required<TicketCardItem>();
  readonly draggable = input(false);
  readonly showSiteName = input(true);
  readonly dragStarted = output<void>();
  readonly dragEnded = output<void>();
  readonly ticketClicked = output<void>();

  private readonly maintenant = signal(Date.now());
  private readonly destroyRef = inject(DestroyRef);
  private isDragging = false;
  private finGlisserTimeout?: ReturnType<typeof setTimeout>;

  readonly dateCreation = computed(
    () => formaterDateCarte(this.ticket().dateCreation) ?? this.ticket().dateDebut,
  );
  readonly ancienneteMessage = computed(() =>
    formaterAnciennete(this.ticket().dateDernierMessage, this.maintenant()),
  );
  readonly ancienneteIntervention = computed(() =>
    formaterAnciennete(this.ticket().dateDerniereIntervention, this.maintenant()),
  );
  readonly sansMessage = computed(
    () => this.ticket().nbMessages === 0 || this.ticket().dateDernierMessage === null,
  );
  readonly sansIntervention = computed(
    () => this.ticket().nbBonsIntervention === 0 || this.ticket().dateDerniereIntervention === null,
  );
  readonly dateIntervention = computed(() =>
    formaterDateCarte(this.ticket().dateInterventionPlanifiee, true),
  );
  readonly interventionDepassee = computed(() =>
    dateInterventionDepassee(this.ticket().dateInterventionPlanifiee, this.maintenant()),
  );
  readonly statutIntervention = computed(() =>
    this.dateIntervention()
      ? `Intervention planifiée le ${this.dateIntervention()}${this.interventionDepassee() ? ' — date dépassée' : ''}`
      : 'Aucune intervention planifiée',
  );
  readonly indicationParDefaut = 'Valeur par défaut — donnée API indisponible';

  constructor() {
    this.destroyRef.onDestroy(() => clearTimeout(this.finGlisserTimeout));
    // L'horloge ne tourne que dans le navigateur et s'arrête quand un silo détruit ses cartes.
    afterNextRender(() => {
      const intervalle = setInterval(() => this.maintenant.set(Date.now()), 60_000);
      this.destroyRef.onDestroy(() => clearInterval(intervalle));
    });
  }

  onDragStart(event: DragEvent) {
    if (
      (event.target instanceof Element && event.target.closest('app-intervention-acces')) ||
      !this.draggable()
    ) {
      event.preventDefault();
      return;
    }
    clearTimeout(this.finGlisserTimeout);
    this.isDragging = true;
    if (event.dataTransfer) {
      event.dataTransfer.setData('text/plain', this.ticket().id ?? this.ticket().reference);
      event.dataTransfer.effectAllowed = 'move';
    }
    this.dragStarted.emit();
  }

  /** Ignore le clic synthétique qui peut suivre un dépôt, avant de réautoriser l'ouverture. */
  onDragEnd() {
    this.finGlisserTimeout = setTimeout(() => {
      this.isDragging = false;
    }, 50);
    this.dragEnded.emit();
  }

  onClick() {
    if (!this.isDragging) this.ticketClicked.emit();
  }
}
